import React, { useState, useEffect } from "react";
import { trimmingService } from "../services/trimmingService";
import { userService } from "../services/userService";

const TrimmingManagement = () => {
  const [trimmings, setTrimmings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTrimming, setEditingTrimming] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formDetails, setFormDetails] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [formData, setFormData] = useState({
    user_id: "",
    admin_id: "",
    count: "",
    reason: "",
    permission_status: "Raised",
    remarkbygardenassistant: "",
    purpose: "",
    Permission_Type: "",
    division: "",
  });

  useEffect(() => {
    loadTrimmings(currentPage);
    loadUsers();
  }, [currentPage]);

  const loadTrimmings = async (page = 1) => {
    try {
      setLoading(true);
      const response = await trimmingService.getAllTrimmings(page, 20);
      setTrimmings(response.data.data || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Error loading trimmings:", error);
      alert(
        "Error loading trimmings: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userService.getUsers();
      setUsers(response.data.data || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingTrimming) {
        await trimmingService.updateTrimming(
          editingTrimming.permission_id || editingTrimming.id,
          formData
        );
        alert("Trimming application updated successfully!");
      } else {
        await trimmingService.createTrimming(formData);
        alert("Trimming application created successfully!");
      }
      setShowForm(false);
      setEditingTrimming(null);
      setFormData({
        user_id: "",
        admin_id: "",
        count: "",
        reason: "",
        permission_status: "Raised",
        remarkbygardenassistant: "",
        purpose: "",
        Permission_Type: "",
        division: "",
      });
      loadTrimmings(currentPage);
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (trimming) => {
    setEditingTrimming(trimming);
    setFormData({
      user_id: trimming.user_id || "",
      admin_id: trimming.admin_id || "",
      count: trimming.tree_count || trimming.count || "",
      reason: trimming.reason || "",
      permission_status:
        trimming.status || trimming.permission_status || "Raised",
      remarkbygardenassistant:
        trimming.remark || trimming.remarkbygardenassistant || "",
      purpose: trimming.purpose || "",
      Permission_Type: trimming.Permission_Type || "Trimming Permission",
      division: trimming.division || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this trimming application?"
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      await trimmingService.deleteTrimming(id);
      alert("Trimming application deleted successfully!");
      loadTrimmings(currentPage);
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleViewForm = async (trimming) => {
    try {
      setLoadingForm(true);
      const response = await trimmingService.getTrimmingById(
        trimming.permission_id || trimming.id
      );
      setFormDetails(response.data.data);
      setShowFormModal(true);
    } catch (error) {
      console.error("Error loading form details:", error);
      alert(
        "Error loading form details: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoadingForm(false);
    }
  };

  // Parse tree details from the form data
  const parseTreeDetails = (data) => {
    const trees = [];
    try {
      // Try to parse tree_name, count, agesapproved, namesapproved
      const treeNames = data.tree_name
        ? typeof data.tree_name === "string"
          ? data.tree_name.split(",")
          : [data.tree_name]
        : [];
      const counts = data.count
        ? typeof data.count === "string"
          ? data.count.split(",")
          : [data.count]
        : [];
      const ages = data.agesapproved
        ? typeof data.agesapproved === "string"
          ? data.agesapproved.split(",")
          : [data.agesapproved]
        : [];
      const names = data.namesapproved
        ? typeof data.namesapproved === "string"
          ? data.namesapproved.split(",")
          : [data.namesapproved]
        : [];

      const maxLength = Math.max(
        treeNames.length,
        counts.length,
        ages.length,
        names.length
      );

      for (let i = 0; i < maxLength; i++) {
        if (treeNames[i] || counts[i]) {
          trees.push({
            treeNo: i + 1,
            treeName: treeNames[i] || names[i] || "N/A",
            noOfTrees: counts[i] || "1",
            age: ages[i] || "N/A",
          });
        }
      }
    } catch (error) {
      console.error("Error parsing tree details:", error);
      // If parsing fails, show at least one row with available data
      if (data.count || data.tree_name) {
        trees.push({
          treeNo: 1,
          treeName: data.tree_name || data.namesapproved || "N/A",
          noOfTrees: data.count || "1",
          age: data.agesapproved || "N/A",
        });
      }
    }
    return trees.length > 0
      ? trees
      : [
          {
            treeNo: 1,
            treeName: "N/A",
            noOfTrees: data.count || "1",
            age: "N/A",
          },
        ];
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            Trimming Application Management
          </h2>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingTrimming(null);
              setFormData({
                user_id: "",
                admin_id: "",
                count: "",
                reason: "",
                permission_status: "Raised",
                remarkbygardenassistant: "",
                purpose: "",
                Permission_Type: "",
                division: "",
              });
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Trimming Application
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">
                {editingTrimming
                  ? "Edit Trimming Application"
                  : "Add Trimming Application"}
              </h3>
              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    User *
                  </label>
                  <select
                    required
                    value={formData.user_id}
                    onChange={(e) =>
                      setFormData({ ...formData, user_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select User</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.mobile})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Admin ID
                  </label>
                  <input
                    type="number"
                    value={formData.admin_id}
                    onChange={(e) =>
                      setFormData({ ...formData, admin_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Tree Count
                  </label>
                  <input
                    type="number"
                    value={formData.count}
                    onChange={(e) =>
                      setFormData({ ...formData, count: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Permission Status
                  </label>
                  <select
                    value={formData.permission_status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        permission_status: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Raised">Raised</option>
                    <option value="Reviewed">Reviewed</option>
                    <option value="Panchanama Scheduled">
                      Panchanama Scheduled
                    </option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Reason
                  </label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    rows="3"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Remark (Garden Inspector)
                  </label>
                  <textarea
                    value={formData.remarkbygardenassistant}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        remarkbygardenassistant: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    rows="3"
                  />
                </div>
                <div className="col-span-2 flex gap-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading
                      ? "Saving..."
                      : editingTrimming
                      ? "Update"
                      : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingTrimming(null);
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading && !showForm ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Sr. No.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Permission ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Permission Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Purpose
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Applied On
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Division
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Permission Form
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trimmings.map((trimming, index) => (
                    <tr key={trimming.permission_id || trimming.id}>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(currentPage - 1) * 20 + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {trimming.permission_id || trimming.id}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.name || trimming.user_name || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.Permission_Type || "Trimming Permission"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.purpose
                          ? trimming.purpose.replace(/_/g, " ")
                          : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.created_at
                          ? new Date(trimming.created_at).toLocaleDateString(
                              "en-GB"
                            )
                          : "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trimming.division || "-"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            (trimming.status || trimming.permission_status) ===
                              "Approved" ||
                            (trimming.status || trimming.permission_status) ===
                              "approved"
                              ? "bg-blue-100 text-blue-800"
                              : (trimming.status ||
                                  trimming.permission_status) === "Rejected" ||
                                (trimming.status ||
                                  trimming.permission_status) === "rejected"
                              ? "bg-red-100 text-red-800"
                              : (trimming.status ||
                                  trimming.permission_status) === "Completed" ||
                                (trimming.status ||
                                  trimming.permission_status) === "completed"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {trimming.status ||
                            trimming.permission_status ||
                            "Raised"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewForm(trimming)}
                          className="text-blue-600 hover:text-blue-900 underline"
                        >
                          View Form
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEdit(trimming)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(trimming.permission_id || trimming.id)
                          }
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {(currentPage - 1) * 20 + 1} to{" "}
                  {Math.min(currentPage * 20, pagination.total)} of{" "}
                  {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={!pagination.hasPrev}
                    className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm">
                    Page {currentPage} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(pagination.totalPages, prev + 1)
                      )
                    }
                    disabled={!pagination.hasNext}
                    className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Permission Form Modal */}
        {showFormModal && formDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                  Tree Trimming Application Form
                </h2>
                <button
                  onClick={() => {
                    setShowFormModal(false);
                    setFormDetails(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="p-6">
                {/* Permission Details */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-4 border-b pb-2">
                    Permission Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Permission ID
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.permission_id || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.user_name ||
                          `${formDetails.firstname || ""} ${
                            formDetails.lastname || ""
                          }`.trim() ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.user_email || formDetails.email || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Mobile
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.user_mobile || formDetails.mobile || "-"}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Address
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.address ||
                          `${formDetails.street || ""} ${
                            formDetails.area || ""
                          }`.trim() ||
                          `${formDetails.first_name || ""} ${
                            formDetails.last_name || ""
                          }`.trim() ||
                          "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Area
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.area || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Division
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.division || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Ward No
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.wardno || formDetails.ward_no || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Purpose
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.purpose
                          ? formDetails.purpose.replace(/_/g, " ")
                          : "-"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Permission Status
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.permission_status ||
                          formDetails.status ||
                          "-"}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Reason
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {formDetails.reason || "-"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Requested Tree Details */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-4 border-b pb-2">
                    Requested Tree Details
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tree No.
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Tree Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            No. of Trees
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {parseTreeDetails(formDetails).map((tree) => (
                          <tr key={tree.treeNo}>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {tree.treeNo}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {tree.treeName}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {tree.noOfTrees}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Approval Tables */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-4 border-b pb-2">
                    Approval Tables
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Officer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Remark
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            Garden Inspector
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formDetails.statusbygardenassistant || "Raised"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formDetails.remarkbygardenassistant || "-"}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            Divisional Officer
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formDetails.statusbydivisionalofficer || "Raised"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formDetails.remarkbydivisionalofficer || "-"}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            Garden Superintendent
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formDetails.statusbygardensuperintendent ||
                              "Raised"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formDetails.remarkbygardensuperintendent || "-"}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            Additional Commissioner
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formDetails.statusbyadditional || "Raised"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formDetails.remarkbyadditional || "-"}
                          </td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            Commissioner
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {formDetails.statusbycommissioner ||
                              formDetails.statusbycommisioner ||
                              "Raised"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formDetails.remarkbycommissioner || "-"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Documents */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-4 border-b pb-2">
                    Documents
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {formDetails.panchanama && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Panchanama
                        </label>
                        <a
                          href={formDetails.panchanama}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 underline text-sm"
                        >
                          View Document
                        </a>
                      </div>
                    )}
                    {formDetails.consent_letter && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Consent Letter
                        </label>
                        <a
                          href={formDetails.consent_letter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 underline text-sm"
                        >
                          View Document
                        </a>
                      </div>
                    )}
                    {formDetails.certificate7_12 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Certificate 7/12
                        </label>
                        <a
                          href={formDetails.certificate7_12}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 underline text-sm"
                        >
                          View Document
                        </a>
                      </div>
                    )}
                    {formDetails.form_c && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Form C
                        </label>
                        <a
                          href={formDetails.form_c}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 underline text-sm"
                        >
                          View Document
                        </a>
                      </div>
                    )}
                    {formDetails.form_d && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Form D
                        </label>
                        <a
                          href={formDetails.form_d}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 underline text-sm"
                        >
                          View Document
                        </a>
                      </div>
                    )}
                    {formDetails.tree_images && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Tree Images
                        </label>
                        <a
                          href={formDetails.tree_images}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-900 underline text-sm"
                        >
                          View Images
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Application History */}
                {formDetails.history && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4 border-b pb-2">
                      Application History
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                        {formDetails.history}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrimmingManagement;
